import { useMemo, useState } from "react";
import { X, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { unitCompletionWarnings, derivedStatus } from "@/lib/unitStatus";

/**
 * Per-unit "Merkitse valmiiksi" dialog.
 *
 *  - Reuses the same advisory-warning model as MarkAllDoneDialog: warnings
 *    are shown but the user can always proceed.
 *  - On confirm: parent runs the PATCH (this dialog stays presentational).
 *  - After success: parent flips dialog into "done" phase, optionally
 *    showing a "Jatka seuraavaan: <next unit>" CTA.
 */
export default function MarkUnitDoneDialog({
  unit,
  nextUnit, // {id, label} or null
  onClose,
  onConfirm, // async () => void (parent does the PATCH)
  onContinueToNext, // () => void (parent scrolls + expands next pending)
}) {
  const [phase, setPhase] = useState("review"); // review | done
  const [busy, setBusy] = useState(false);

  const warnings = useMemo(() => unitCompletionWarnings(unit), [unit]);
  const allOk = warnings.length === 0 || derivedStatus(unit) === "done";

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      setPhase("done");
    } finally {
      setBusy(false);
    }
  };

  if (phase === "done") {
    return (
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="mark-unit-done-dialog">
        <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl shadow-xl p-6 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">Yksikkö merkitty valmiiksi</h3>
          <p className="mt-1 text-sm text-slate-500">{unit.label}</p>

          <div className="mt-6 flex flex-col gap-2">
            {nextUnit ? (
              <button
                type="button"
                onClick={() => {
                  onContinueToNext?.(nextUnit);
                  onClose();
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                data-testid="mark-unit-continue-next"
              >
                Jatka seuraavaan: {nextUnit.label}
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <div className="text-sm text-emerald-700 font-semibold">🎉 Kaikki yksiköt käsitelty!</div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"
              data-testid="mark-unit-close-after-done"
            >
              Sulje
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="mark-unit-done-dialog">
      <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            {allOk ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
            )}
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">
                {allOk ? "Merkitäänkö valmiiksi?" : "Huomio"}
              </h3>
              <div className="text-xs text-slate-500">{unit.label}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="mark-unit-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {allOk ? (
            <div className="text-sm text-slate-700">Yksikössä on kaikki tarvittavat tiedot. Merkataanko valmiiksi?</div>
          ) : (
            <>
              <div className="text-sm text-slate-700 mb-3">
                Yksikössä on vielä täyttämättömiä kohtia. <strong>Voit silti merkitä sen valmiiksi</strong> — tämä on vain muistutus.
              </div>
              <ul className="space-y-1.5 rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid="mark-unit-warnings">
                {warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50"
            data-testid="mark-unit-back"
          >
            {allOk ? "Peruuta" : "Palaa"}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
            data-testid="mark-unit-confirm"
          >
            {busy ? "Tallennetaan..." : allOk ? "Merkitse valmiiksi" : "Merkitse valmiiksi silti"}
          </button>
        </div>
      </div>
    </div>
  );
}
